import React, { Suspense, lazy } from 'react';

import { setLoadingState } from 'prequalificationV2/actions';
import { Document } from 'prequalificationV2/actions/documents';
import Dropzone from 'components/Dropzone';
import { PDF_FILE_TYPE } from 'constants/constants';
import { DocumentCategory } from 'enums/DocumentCategory';
import { SuccessNotification } from 'enums/SuccessNotification';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import * as notificationManager from 'notifications';
import { FailureNotificationType } from 'notifications';
import { getBorrowerNameForDocument, getDocumentFullName } from 'prequalificationV2/utils/documentsUtils';
import { createFileWithName } from 'prequalificationV2/utils/fileHelper';
import { updateDocuments } from 'prequalificationV2/utils/updateDocuments';
import { documentsApi, ExtractionDocumentType } from 'api/DocumentsApi';
import { SUPPORTED_DOCUMENT_HEADER_NAMES_TO_VALIDATION } from 'components/LoanPortal/UploadDocuments/DocumentIdBlock/documentIdBlock.constants';
import { applicationApi } from 'prequalificationV2/api/ApplicationApi';
import { DocumentPrefix } from 'enums/DocumentPrefix';

import styles from '../UploadDocuments.module.scss';

const DocumentIdBlock = lazy(() => import('../DocumentIdBlock'));

export interface UploadCategoryProps {
  category: DocumentCategory;
  documents: Document[];
  title?: string;
}

const DROPZONE_EXTENSIONS = [
  'image/png',
  'image/jpeg',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.doc',
  '.docx',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xls',
  '.xlsx',
];

const getCategoryLabel = (category: DocumentCategory) => {
  switch (category) {
    case DocumentCategory.Funding:
      return 'Account used to fund your loan proceeds must only be a checking account';
    default:
      return '';
  }
};

const DOCUMENTS_TO_EXTRACT = [
  {
    documentHeader: 'borrower_home_improvement_estimates_or_contracts_name',
    documentType: ExtractionDocumentType.WorkOrder,
  },
];

const UploadDocumentsCategory = ({
  documents,
  category,
  title,
  children,
}: React.PropsWithChildren<UploadCategoryProps>) => {
  const dispatch = useAppDispatch();
  const { applicationId } = useAppSelector((state) => state.application);
  const borrower = useAppSelector((state) => state.borrower);
  const coBorrower = useAppSelector((state) => state.coBorrower);
  const documentCategoryRequired = !!documents.length;

  const handleFiles = async (initialFiles: File[], documentHeader: string, documentCategory: DocumentCategory) => {
    try {
      dispatch(setLoadingState({ type: `UPLOADING_${documentHeader}`, loading: true }));

      const { borrowerApplication } = await applicationApi.getActiveApplication();

      const filesToUpload: File[] = [];

      for (const initialFile of initialFiles) {
        const newFile = await createFileWithName(
          initialFile,
          getDocumentFullName(
            initialFile,
            borrowerApplication?.variables[documentHeader] as string,
            documentCategory,
            documentCategory.startsWith('Coborrower') || documentHeader.startsWith(DocumentPrefix.CoborrowerUtilityBill)
              ? getBorrowerNameForDocument(coBorrower.firstName, coBorrower.lastName)
              : getBorrowerNameForDocument(borrower.firstName, borrower.lastName),
          ),
        );

        const text = await newFile.text();

        if (initialFile.type === PDF_FILE_TYPE && text.includes('Encrypt')) {
          notificationManager.notifyFailure(FailureNotificationType.EncryptedDocument);
          await dispatch(setLoadingState({ type: `UPLOADING_${documentHeader}`, loading: false }));

          return;
        }

        filesToUpload.push(newFile);
      }

      const documentToExtract = DOCUMENTS_TO_EXTRACT.find((document) => document.documentHeader === documentHeader);

      const updatedApplication = await documentsApi.uploadDocuments({
        applicationId: applicationId!,
        documentVariablePrefix: documentHeader.replace('name', 'status'),
        files: filesToUpload,
        documentCategory: documentCategory,
        extractDocument: !!documentToExtract,
        documentType: documentToExtract?.documentType,
      });

      await applicationApi.updateApplicationLastActivityDate(applicationId!);

      if (updatedApplication) {
        updateDocuments(updatedApplication, dispatch);
      }

      notificationManager.notifySuccess(SuccessNotification.DocumentUploadedSuccessfully);
    } catch (err) {
      notificationManager.notifyFailure(FailureNotificationType.DocumentNotUploaded);
    } finally {
      dispatch(setLoadingState({ type: `UPLOADING_${documentHeader}`, loading: false }));
    }
  };

  return (
    <>
      {title && documentCategoryRequired && <div className={styles.categoryTitle}>{title}</div>}
      <p className={styles.categoryLabel}>{getCategoryLabel(category as DocumentCategory)}</p>
      <div className={styles.category}>
        {documents.map((document) => {
          if (SUPPORTED_DOCUMENT_HEADER_NAMES_TO_VALIDATION.includes(document.header)) {
            return (
              <Suspense key={document.header} fallback={<div></div>}>
                <DocumentIdBlock document={document} />
              </Suspense>
            );
          }

          return (
            <Dropzone
              key={document.header}
              extensions={DROPZONE_EXTENSIONS}
              handleFiles={handleFiles}
              header={document.header}
              status={document.status}
              name={document.name}
              category={document.category}
            />
          );
        })}
        {children}
      </div>
    </>
  );
};

export default UploadDocumentsCategory;
